/**
 * Silabeador fonológico en español para Clarisa Hub.
 * Descompone palabras en sílabas siguiendo las reglas de la RAE:
 * - Ataque, núcleo y coda
 * - Dígrafos inseparables (ch, ll, rr)
 * - Grupos consonánticos inseparables (bl, br, cl, cr, dr, fl, fr, gl, gr, pl, pr, tr)
 * - Diptongos, triptongos e hiatos (vocales abiertas vs cerradas / tildes)
 */

export function syllabifySpanish(word: string): string[] {
  if (!word || typeof word !== 'string') return [];
  const clean = word.trim();
  if (!clean) return [];

  // Si contiene espacios o guiones, separar y procesar
  if (clean.includes(' ')) {
    return clean.split(/\s+/).flatMap((w) => syllabifySpanish(w));
  }

  const strongVowels = '[aáeéoóAÁEÉOÓ]';
  const accentedWeak = '[íÍúÚ]';
  const allVowels = '[aáeéoóAÁEÉOÓiIuUüÜyYíÍúÚ]';

  const isVowel = (ch: string) => new RegExp(`^${allVowels}$`).test(ch);
  const isStrong = (ch: string) => new RegExp(`^(${strongVowels}|${accentedWeak})$`).test(ch);

  const n = clean.length;
  const nuclei: Array<{ start: number; end: number }> = [];

  let i = 0;
  while (i < n) {
    if (isVowel(clean[i])) {
      const start = i;
      let end = i + 1;

      // Diptongos y triptongos
      while (end < n && isVowel(clean[end])) {
        const prev = clean[end - 1];
        const curr = clean[end];

        // Dos vocales abiertas / fuertes contiguas forman hiato
        if (isStrong(prev) && isStrong(curr)) {
          break;
        }
        // Vocales idénticas forman hiato
        if (prev.toLowerCase() === curr.toLowerCase()) {
          break;
        }

        end++;
        if (end - start > 3) break;
      }
      nuclei.push({ start, end });
      i = end;
    } else {
      i++;
    }
  }

  // Monosílabo o sin vocales
  if (nuclei.length <= 1) {
    return [clean];
  }

  const isDigraph = (str: string) => {
    const s = str.toLowerCase();
    return s === 'ch' || s === 'll' || s === 'rr';
  };

  const isInseparableCluster = (str: string) => {
    const s = str.toLowerCase();
    if (isDigraph(s)) return true;
    if (s.length !== 2) return false;
    const c1 = s[0];
    const c2 = s[1];
    return 'bcdfptg'.includes(c1) && 'lr'.includes(c2) && !(c1 === 'd' && c2 === 'l') && !(c1 === 't' && c2 === 'l');
  };

  const splitPoints: number[] = [];

  for (let k = 0; k < nuclei.length - 1; k++) {
    const n1 = nuclei[k];
    const n2 = nuclei[k + 1];

    const interConsonants = clean.slice(n1.end, n2.start);
    const numCons = interConsonants.length;

    if (numCons === 0) {
      // Hiato directo entre vocales
      splitPoints.push(n1.end);
    } else if (numCons === 1) {
      // V-CV
      splitPoints.push(n1.end);
    } else if (numCons === 2) {
      if (isInseparableCluster(interConsonants)) {
        // V-CCV
        splitPoints.push(n1.end);
      } else {
        // VC-CV
        splitPoints.push(n1.end + 1);
      }
    } else if (numCons === 3) {
      const lastTwo = interConsonants.slice(-2);
      if (isInseparableCluster(lastTwo)) {
        // VC-CCV (ej. com-prar)
        splitPoints.push(n1.end + 1);
      } else {
        // VCC-CV (ej. trans-porte)
        splitPoints.push(n1.end + 2);
      }
    } else if (numCons === 4) {
      // VCC-CCV (ej. cons-truir)
      splitPoints.push(n1.end + 2);
    } else {
      splitPoints.push(n1.end + 1);
    }
  }

  const syllables: string[] = [];
  let prevSplit = 0;
  for (const sp of splitPoints) {
    syllables.push(clean.slice(prevSplit, sp));
    prevSplit = sp;
  }
  syllables.push(clean.slice(prevSplit));

  return syllables;
}
